import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon } from 'lucide-react';
export function AudienceSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      x: -20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const steps = [{
    num: '1',
    text: 'Descarga el sistema y localiza tu clúster de sedes según tus boletos.'
  }, {
    num: '2',
    text: 'Ejecuta el Protocolo de Documentación para asegurar tus permisos de entrada meses antes.'
  }, {
    num: '3',
    text: 'Aplica los filtros de transporte "Last Mile" para llegar a cada estadio sin pagar tarifas dinámicas de Uber.'
  }];
  return <section className="py-24 bg-navy-950 border-t border-navy-800" data-id="element-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2">
        {/* Audience Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-24" data-id="element-3">
          {/* Who is it for */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-navy-900 rounded-2xl p-8 border border-pitch-600/30 relative overflow-hidden" data-id="element-4">
            <div className="absolute top-0 right-0 w-32 h-32 bg-pitch-500/10 rounded-bl-full" data-id="element-5" />
            <h3 className="text-2xl font-bold text-white mb-8 uppercase flex items-center gap-3" data-id="element-6">
              <CheckCircleIcon className="w-8 h-8 text-pitch-500" data-id="element-7" />
              Para Quién Es Esta Guía
            </h3>
            <ul className="space-y-6 relative z-10" data-id="element-8">
              <li className="flex items-start gap-4" data-id="element-9">
                <CheckCircleIcon className="w-6 h-6 text-pitch-500 flex-shrink-0 mt-0.5" data-id="element-10" />
                <span className="text-slate-300" data-id="element-11">
                  Aficionados que viajan en grupo y necesitan optimizar gastos.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-12">
                <CheckCircleIcon className="w-6 h-6 text-pitch-500 flex-shrink-0 mt-0.5" data-id="element-13" />
                <span className="text-slate-300" data-id="element-14">
                  Viajeros internacionales que nunca han cruzado las fronteras
                  de Norteamérica.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-15">
                <CheckCircleIcon className="w-6 h-6 text-pitch-500 flex-shrink-0 mt-0.5" data-id="element-16" />
                <span className="text-slate-300" data-id="element-17">
                  El "líder" del grupo que tiene la responsabilidad de que nadie
                  se quede fuera del estadio.
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Who is it NOT for */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-navy-900 rounded-2xl p-8 border border-red-900/30 relative overflow-hidden" data-id="element-18">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full" data-id="element-19" />
            <h3 className="text-2xl font-bold text-white mb-8 uppercase flex items-center gap-3" data-id="element-20">
              <XCircleIcon className="w-8 h-8 text-red-500" data-id="element-21" />
              Para Quién NO Es
            </h3>
            <ul className="space-y-6 relative z-10" data-id="element-22">
              <li className="flex items-start gap-4" data-id="element-23">
                <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-24" />
                <span className="text-slate-300" data-id="element-25">
                  Viajeros con presupuesto ilimitado que tienen chofer privado y
                  jet.
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-26">
                <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-27" />
                <span className="text-slate-300" data-id="element-28">
                  Personas que prefieren la improvisación absoluta y no les
                  importa perder sus vuelos.
                </span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* How it Works */}
        <div className="max-w-4xl mx-auto" data-id="element-29">
          <motion.h3 initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold text-center text-white mb-12 uppercase tracking-wide" data-id="element-30">
            Cómo Funciona
          </motion.h3>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="space-y-6" data-id="element-31">
            {steps.map((step, index) => <motion.div key={index} variants={itemVariants} className="flex items-center gap-6 bg-navy-800/50 border border-navy-700 rounded-2xl p-6" data-id="element-32">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center text-gold-400 font-heading font-bold text-2xl" data-id="element-33">
                  {step.num}
                </div>
                <p className="text-lg text-slate-200" data-id="element-34">{step.text}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}